import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit3,
  FiEye,
  FiMoreVertical,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AudienceMember } from '../../features/audience/audienceSlice';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import DeleteContactModal from './DeleteContactModal';

// Type-cast Feather icons for React compatibility
type IconProps = {
  size?: number | string;
  className?: string;
  onClick?: (e: any) => void;
};

const FiEyeComponent = FiEye as unknown as React.ComponentType<IconProps>;
const FiMoreVerticalComponent = FiMoreVertical as unknown as React.ComponentType<IconProps>;
const FiChevronUpComponent = FiChevronUp as unknown as React.ComponentType<IconProps>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<IconProps>;
const FiSearchComponent = FiSearch as unknown as React.ComponentType<IconProps>;
const FiTrash2Component = FiTrash2 as unknown as React.ComponentType<IconProps>;
const FiEdit3Component = FiEdit3 as unknown as React.ComponentType<IconProps>;

// Portal Component for rendering popups outside the table
const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    children,
    document.body
  );
};

// Interface for field configuration
interface FieldConfig {
  name: string;
  filter: boolean;
}

// Props for the AudienceTable component
interface AudienceTableProps {
  paginatedAudience: AudienceMember[];
  selected: string[];
  toggleSelect: (id: string) => void;
  toggleSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requestSort: (key: keyof AudienceMember) => void;
  getSortIcon: (key: keyof AudienceMember) => React.ReactNode;
  getStatusColor: (status: string) => string;
  fieldConfigs: FieldConfig[];
  nameFilter: string;
  onNameFilterApply: (filterValue: string) => void;
  onNameFilterClear: () => void;
  setSortConfig: (
    config: { key: keyof AudienceMember; direction: 'asc' | 'desc' } | null
  ) => void;
  onContactDeleted?: () => void;
  loading?: boolean;
  showCheckboxes?: boolean;
}

// Component for rendering the audience data table
const AudienceTable: React.FC<AudienceTableProps> = ({
  paginatedAudience,
  selected,
  toggleSelect,
  toggleSelectAll,
  requestSort,
  getSortIcon,
  getStatusColor,
  fieldConfigs,
  nameFilter,
  onNameFilterApply,
  onNameFilterClear,
  setSortConfig,
  onContactDeleted,
  loading = false,
  showCheckboxes = false,
}) => {
  // const dispatch = useDispatch();
  const navigate = useNavigate();
  // State for name filter popup and sorting
  const [showNameFilter, setShowNameFilter] = useState(false);
  const [tempFilterValue, setTempFilterValue] = useState(nameFilter);
  const [sortOption, setSortOption] = useState<'a-z' | 'z-a'>('a-z');
  const filterRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableCellElement>(null);

  // State for delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<AudienceMember | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
  const [showPopup, setShowPopup] = useState<string | null>(null);
  // const moreButtonRef = useRef<HTMLDivElement>(null);

  // State for tag popup
  const [tagPopupPosition, setTagPopupPosition] = useState<{ x: number; y: number } | null>(null);
  const [showTagPopup, setShowTagPopup] = useState<string | null>(null);

  // Closes name filter popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowNameFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Syncs temporary filter value with name filter
  useEffect(() => {
    setTempFilterValue(nameFilter);
  }, [nameFilter]);

  // Maps table headers to AudienceMember keys
  const getHeaderKey = (field: string): keyof AudienceMember => {
    const fieldMap: { [key: string]: keyof AudienceMember } = {
      'Name & Email': 'name',
      Gender: 'gender',
      Contact: 'phone',
      DOB: 'dob',
      Tag: 'tags',
      'Last Activities': 'lastActivity',
      'Engage Score': 'engageScore',
      Status: 'status',
    };
    return fieldMap[field] || 'name';
  };

  // Handles clicks on table headers for sorting or showing name filter
  const handleNameHeaderClick = (field: string) => {
    if (field === 'Name & Email') {
      setShowNameFilter((s) => !s);
    } else {
      requestSort(getHeaderKey(field));
    }
  };

  // Applies name filter and sorting
  const handleApplyFilter = () => {
    onNameFilterApply(tempFilterValue);
    const direction = sortOption === 'a-z' ? 'asc' : 'desc';
    setSortConfig({ key: 'name', direction });
    setShowNameFilter(false);
  };

  // Handles keyboard interactions for filter input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tempFilterValue.trim()) {
      onNameFilterApply(tempFilterValue);
    } else if (e.key === 'Escape') {
      setShowNameFilter(false);
    }
  };

  // Clears filter when input is empty
  useEffect(() => {
    if (tempFilterValue === '') {
      onNameFilterClear();
    }
  }, [tempFilterValue, onNameFilterClear]);

  // Truncates header text for display
  const truncateHeader = (text: string, maxLength = 14) =>
    text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

  // Checks if header needs a tooltip
  const needsTooltip = (text: string) => text.length > 14;

  // Returns full header name for tooltip
  const getFullHeaderName = (configName: string) => {
    return configName === 'Name & Email' ? 'NAME' : configName;
  };

  // Handle three dots click - using fixed positioning relative to viewport
  const handleMoreClick = (e: React.MouseEvent, contact: AudienceMember) => {
    e.stopPropagation();

    // Get the position of the clicked element relative to viewport
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    // Position the popup to the right of the three dots
    const popupX = rect.right + 5;
    const popupY = rect.top;

    // Ensure popup doesn't go off screen
    const viewportWidth = window.innerWidth;
    const popupWidth = 160;

    let finalX = popupX;
    if (finalX + popupWidth > viewportWidth - 10) {
      // If it goes off the right edge, show on the left
      finalX = rect.left - popupWidth - 5;
    }
    if (finalX < 10) {
      finalX = 10;
    }

    setPopupPosition({
      x: finalX,
      y: popupY,
    });
    setShowPopup(showPopup === contact.id ? null : contact.id);
    setContactToDelete(contact);
  };

  // Handle tag hover
  const handleTagHover = (e: React.MouseEvent, contactId: string, tags: string[]) => {
    if (!tags || tags.length === 0) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    // Position below the tag
    let popupX = rect.left;
    let popupY = rect.bottom + 8;

    // Ensure popup doesn't go off screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const popupWidth = 300;
    const popupHeight = Math.min(tags.length * 32 + 40, 200);

    if (popupX + popupWidth > viewportWidth - 10) {
      popupX = viewportWidth - popupWidth - 10;
    }
    if (popupX < 10) {
      popupX = 10;
    }
    if (popupY + popupHeight > viewportHeight - 10) {
      popupY = rect.top - popupHeight - 8;
    }

    setTagPopupPosition({ x: popupX, y: popupY });
    setShowTagPopup(contactId);
  };

  const handleTagLeave = () => {
    setShowTagPopup(null);
  };

  const handleEditClick = () => {
    setShowPopup(null);
    if (contactToDelete) {
      // Fetch the full contact data with segments
      const fetchContactWithSegments = async () => {
        try {
          const { token, tenantId, userId } = getAuthContext();
          if (!token) return;

          const response = await fetch(
            `${BASE_URL}/v1/audience-insights/contacts/${contactToDelete.id}`,
            {
              method: 'GET',
              headers: {
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            navigate(`/audience/edit/${contactToDelete.id}`, {
              state: {
                contactData: data,
                segments: data.segments || []
              }
            });
          } else {
            // Fallback to the data we already have
            navigate(`/audience/edit/${contactToDelete.id}`, {
              state: { contactData: contactToDelete }
            });
          }
        } catch (error) {
          // console.error('Failed to fetch contact details:', error);
          toast.error('Failed to fetch contact details. Please try again later.');
          // Fallback to the data we already have
          navigate(`/audience/edit/${contactToDelete.id}`, {
            state: { contactData: contactToDelete }
          });
        }
      };

      fetchContactWithSegments();
    }
  };

  // Handle delete click from popup
  const handleDeleteClick = () => {
    setShowPopup(null);
    setShowDeleteModal(true);
  };

  // Handle actual delete with retry logic
  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        return;
      }


      // Show loading toast
      const loadingToast = toast.loading('Deleting contact...');

      const response = await fetch(
        `${BASE_URL}/v1/audience-insights/contacts/${contactToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      // Update loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success('Contact deleted successfully');
      setShowDeleteModal(false);
      setContactToDelete(null);

      // Call the callback to refresh the list with a slight delay
      if (onContactDeleted) {
        // Add a small delay to ensure server has processed the deletion
        setTimeout(() => {
          onContactDeleted();
        }, 300);
      } else {
        console.warn('⚠️ onContactDeleted callback is not provided');
      }
    } catch (error) {
      // console.error('❌ Failed to delete contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPopup) {
        const target = event.target as HTMLElement;
        if (!target.closest('.popup-menu') && !target.closest('.more-button')) {
          setShowPopup(null);
        }
      }
      if (showTagPopup) {
        const target = event.target as HTMLElement;
        if (!target.closest('.tag-popup') && !target.closest('.tag-trigger')) {
          setShowTagPopup(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup, showTagPopup]);

  // Handle scroll to close popup
  useEffect(() => {
    const handleScroll = () => {
      if (showPopup) {
        setShowPopup(null);
      }
      if (showTagPopup) {
        setShowTagPopup(null);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showPopup, showTagPopup]);

  // Renders cell content based on field type
  const renderCellContent = (audience: AudienceMember, fieldName: string) => {
    switch (fieldName) {
      case 'Name & Email':
        return (
          <>
            <div className='font-medium'>{audience.name}</div>
            <div className='text-xs text-gray-400'>{audience.email}</div>
          </>
        );
      case 'Gender':
        return audience.gender || 'N/A';
      case 'Contact':
        return audience.phone || 'N/A';
      case 'DOB':
        return audience.dob || 'N/A';
      case 'Tag':
        if (audience.tags && audience.tags.length > 0) {
          const tagsString = audience.tags.join(', ');
          const shouldTruncate = tagsString.length > 14;

          return (
            <div
              className="relative inline-block tag-trigger"
              onMouseEnter={(e) => handleTagHover(e, audience.id, audience.tags)}
              onMouseLeave={handleTagLeave}
            >
              <span className="truncate block max-w-[140px] cursor-pointer hover:text-blue-600 transition-colors">
                {shouldTruncate ? tagsString.substring(0, 14) + '...' : tagsString}
              </span>
            </div>
          );
        }
        return 'N/A';
      case 'Last Activities':
        return audience.lastActivity || 'N/A';
      case 'Engage Score': {
        // Use 0 if engagement score is null or undefined
        const score = audience.engageScore !== undefined && audience.engageScore !== null
          ? audience.engageScore
          : 0;
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${score > 70
              ? 'bg-green-100 text-green-600'
              : score > 40
                ? 'bg-yellow-100 text-yellow-600'
                : 'bg-red-100 text-red-600'
              }`}
          >
            {score}
          </span>
        );
      }
      case 'Status':
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
              audience.status
            )}`}
          >
            {audience.status}
          </span>
        );
      default:
        if (audience.customFields && audience.customFields[fieldName]) {
          return audience.customFields[fieldName]?.toString() || 'N/A';
        }
        return 'N/A';
    }
  };

  // Renders the table with audience data
  return (
    <div className='relative overflow-x-auto'>
      <table className='w-full text-left mt-4 border border-gray-200'>
        <thead className='bg-gray-100'>
          <tr className='text-[#657584] text-[12px] border-b'>
            {showCheckboxes && (
              <th className='px-6 py-3 sticky left-0 bg-gray-100 z-20 w-12'>
                <input
                  type='checkbox'
                  onChange={toggleSelectAll}
                  checked={
                    paginatedAudience.length > 0 &&
                    selected.length === paginatedAudience.length
                  }
                />
              </th>
            )}
            {fieldConfigs.map((config) => {
              const fullName = getFullHeaderName(config.name);
              const displayName = truncateHeader(fullName);
              const showTooltip = needsTooltip(fullName);
              // const colSpan = showCheckboxes ? 1 : 0;

              return (
                <th
                  key={config.name}
                  ref={config.name === 'Name & Email' ? headerRef : null}
                  onClick={() =>
                    config.filter && handleNameHeaderClick(config.name)
                  }
                  className={`pl-6 relative ${config.name === 'Gender' || config.name === 'Engage Score'
                    ? 'pr-6'
                    : config.name === 'Last Activities'
                      ? 'pr-10'
                      : 'pr-20'
                    } py-3 ${config.filter ? 'cursor-pointer hover:bg-gray-200' : 'cursor-default'
                    } font-normal ${config.name === 'Name & Email' && showCheckboxes
                      ? 'sticky left-12 bg-gray-100 z-20'
                      : config.name === 'Name & Email' && !showCheckboxes
                        ? 'sticky left-0 bg-gray-100 z-20'
                        : ''
                    } group`}
                  style={{
                    ...(config.name === 'Name & Email' && showCheckboxes
                      ? { left: '48px' }
                      : config.name === 'Name & Email' && !showCheckboxes
                        ? { left: '0px' }
                        : {})
                  }}
                >
                  <div className='flex items-center gap-1 relative'>
                    <span className='truncate'>{displayName}</span>
                    {config.filter && config.name === 'Name & Email' ? (
                      showNameFilter ? (
                        <FiChevronUpComponent
                          size={16}
                          className='text-gray-400'
                        />
                      ) : (
                        <FiChevronDownComponent
                          size={16}
                          className='text-gray-400'
                        />
                      )
                    ) : (
                      config.filter && getSortIcon(getHeaderKey(config.name))
                    )}

                    {/* Tooltip for long headers */}
                    {showTooltip && (
                      <div className='group-hover:opacity-100 opacity-0 transition-opacity duration-200 absolute z-30 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap -bottom-10 left-1/2 transform -translate-x-1/2 pointer-events-none shadow-lg'>
                        {fullName}
                        <div className='absolute bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-l-transparent border-r-4 border-r-transparent border-b-4 border-b-gray-900'></div>
                      </div>
                    )}

                    {/* Name filter popup */}
                    {showNameFilter && config.name === 'Name & Email' && (
                      <div
                        ref={filterRef}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className='absolute bg-white border border-gray-200 rounded-lg shadow-lg w-64 p-0 z-50'
                        style={{
                          position: 'fixed',
                          top: headerRef.current
                            ? headerRef.current.getBoundingClientRect().bottom +
                            5 +
                            'px'
                            : '100px',
                          left: headerRef.current
                            ? headerRef.current.getBoundingClientRect().left +
                            'px'
                            : '50%',
                        }}
                      >
                        {/* Search input and button */}
                        <div className='p-3 border-b border-gray-200'>
                          <div className='flex gap-2'>
                            <div className='relative flex-1'>
                              <FiSearchComponent
                                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'
                                size={16}
                              />
                              <input
                                type='text'
                                value={tempFilterValue}
                                onChange={(e) =>
                                  setTempFilterValue(e.target.value)
                                }
                                onKeyDown={handleKeyPress}
                                placeholder='Search'
                                className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                              />
                            </div>
                            <button
                              onClick={() => {
                                onNameFilterApply(tempFilterValue);
                                setShowNameFilter(false);
                              }}
                              disabled={!tempFilterValue.trim()}
                              className={`px-3 py-2 text-sm rounded ${tempFilterValue.trim()
                                ? 'bg-[#00A4BD] text-white hover:bg-[#008A9E]'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                              Search
                            </button>
                          </div>
                        </div>

                        {/* Sort options */}
                        <div className='p-3'>
                          <div className='text-sm font-normal text-[#9FA9B2] mb-2'>
                            Sort by
                          </div>
                          <div className='space-y-2'>
                            <label className='flex items-center space-x-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='sortOption'
                                value='a-z'
                                checked={sortOption === 'a-z'}
                                onChange={(e) =>
                                  setSortOption(e.target.value as 'a-z')
                                }
                                className='text-blue-600 focus:ring-blue-500'
                              />
                              <span className='text-sm text-gray-700'>A-Z</span>
                            </label>
                            <label className='flex items-center space-x-2 cursor-pointer'>
                              <input
                                type='radio'
                                name='sortOption'
                                value='z-a'
                                checked={sortOption === 'z-a'}
                                onChange={(e) =>
                                  setSortOption(e.target.value as 'z-a')
                                }
                                className='text-blue-600 focus:ring-blue-500'
                              />
                              <span className='text-sm text-gray-700'>Z-A</span>
                            </label>
                          </div>
                        </div>

                        {/* Apply button */}
                        <div className='p-3 flex justify-start space-x-2'>
                          <button
                            onClick={handleApplyFilter}
                            disabled={!sortOption}
                            className={`px-4 py-2 text-sm rounded ${sortOption
                              ? 'bg-[#00A4BD] text-white hover:bg-[#008A9E]'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              }`}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
            <th className='px-6 py-3 font-normal sticky right-0 bg-gray-100 z-20 w-24'>
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
          {paginatedAudience.length > 0 ? (
            paginatedAudience.map((a) => (
              <tr key={a.id} className='border-b hover:bg-gray-50 relative'>
                {showCheckboxes && (
                  <td className='px-6 py-4 sticky left-0 bg-white z-10'>
                    <input
                      type='checkbox'
                      checked={selected.includes(a.id)}
                      onChange={() => toggleSelect(a.id)}
                    />
                  </td>
                )}
                {fieldConfigs.map((config) => (
                  <td
                    key={config.name}
                    className={`px-6 py-4 ${config.name === 'Name & Email' && showCheckboxes
                      ? 'sticky left-12 bg-white z-10'
                      : config.name === 'Name & Email' && !showCheckboxes
                        ? 'sticky left-0 bg-white z-10'
                        : ''
                      }`}
                    style={{
                      ...(config.name === 'Name & Email' && showCheckboxes
                        ? { left: '48px' }
                        : config.name === 'Name & Email' && !showCheckboxes
                          ? { left: '0px' }
                          : {})
                    }}
                  >
                    {renderCellContent(a, config.name)}
                  </td>
                ))}
                <td className='px-6 py-4 sticky right-0 bg-white z-10'>
                  <div className='flex items-center justify-start space-x-3'>
                    <button
                      onClick={() => navigate(`/audience/view/${a.id}`)}
                      className='text-teal-600 flex items-center gap-1 hover:text-teal-800'
                    >
                      <FiEyeComponent size={16} />
                      <span className='text-sm'>View</span>
                    </button>
                    <div className='relative'>
                      <FiMoreVerticalComponent
                        className='text-gray-400 cursor-pointer hover:text-gray-600 more-button'
                        onClick={(e) => handleMoreClick(e, a)}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={fieldConfigs.length + (showCheckboxes ? 2 : 1)}
                className='px-6 py-8 text-center text-gray-500'
              >
                {loading ? 'Loading...' : 'No contacts found in this segment'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Popup menu - Rendered via Portal to avoid table stacking context */}
      {showPopup && popupPosition && (
        <Portal>
          <div
            className='popup-menu'
            style={{
              position: 'fixed',
              top: popupPosition.y,
              left: popupPosition.x,
              zIndex: 99999,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              minWidth: '160px',
              padding: '4px 0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleEditClick}
              style={{
                width: '100%',
                padding: '8px 16px',
                textAlign: 'left',
                fontSize: '14px',
                color: '#3F5265',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FiEdit3Component size={16} />
              Edit
            </button>
            <button
              onClick={handleDeleteClick}
              style={{
                width: '100%',
                padding: '8px 16px',
                textAlign: 'left',
                fontSize: '14px',
                color: '#dc2626',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FiTrash2Component size={16} />
              Delete
            </button>
          </div>
        </Portal>
      )}

      {/* Tag Popup - Rendered via Portal */}
      {showTagPopup && tagPopupPosition && (
        <Portal>
          <div
            className='tag-popup'
            style={{
              position: 'fixed',
              top: tagPopupPosition.y,
              left: tagPopupPosition.x,
              zIndex: 99999,
              backgroundColor: '#1f2937',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              padding: '12px 16px',
              maxWidth: '300px',
              minWidth: '200px',
              maxHeight: '200px',
              overflowY: 'auto',
              pointerEvents: 'none',
            }}
            onMouseEnter={() => {
              // Keep popup open when mouse enters
              const contact = paginatedAudience.find(a => a.id === showTagPopup);
              if (contact && contact.tags) {
                // Popup stays open
              }
            }}
            onMouseLeave={handleTagLeave}
          >
            <div className="flex flex-wrap gap-1.5">
              {paginatedAudience
                .find(a => a.id === showTagPopup)
                ?.tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-600 text-white px-2.5 py-1 rounded-md text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
            </div>
            {/* Arrow pointing to the tag */}
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '20px',
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '6px solid #1f2937',
              }}
            />
          </div>
        </Portal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteContactModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setContactToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        contactName={contactToDelete?.name || ''}
      />
    </div>
  );
};

export default AudienceTable;